import { createContext, useContext } from 'react';
import {
  ChatActorType,
  ChatMessageType,
  ChatTimelineItem,
  ChatType,
  ChatWithLatestMessageType,
  LatestChatMessageType,
  ViewerActorType,
} from './types';

export type UpdateChatParticipantsInput = {
  chatId: string;
  superviseeActorIds: number[];
  shouldCreateNewGroup: boolean;
};

export type UpdateChatParticipantsResult = {
  success: boolean;
  chat?: { id: string } | null;
};

export type EditChatConfig = {
  refetchChat: () => void;
  updateParticipants: (input: UpdateChatParticipantsInput) => Promise<UpdateChatParticipantsResult>;
  updateParticipantsLoading: boolean;
};

export type DeleteChatConfig = {
  deleteChat: (chatId: string) => Promise<{ success: boolean }>;
  deleteChatLoading: boolean;
};

export type ChatsContextProps = {
  activeChatId: string | null;
  chat: ChatType | null;
  chatTimelineItems: ChatTimelineItem[] | null;
  chatTimelineNextCursor: string | null;
  chatRootPath: string;
  chats: ChatWithLatestMessageType[];
  chatsWithUnreadMessages: { id: string }[];
  deleteChatConfig?: DeleteChatConfig;
  editChatConfig?: EditChatConfig;
  enqueueSnackbar: (message: string, options?: { variant: 'error' }) => void;
  fetchMoreChatTimeline: (options: {
    variables: { chatId: string; cursor: string };
  }) => Promise<unknown>;
  findOrCreateChat: (otherActorIds: number | number[]) => Promise<void>;
  findOrCreateChatLoading: boolean;
  onViewChat: (chatId: string) => void;
  onNewMessage: (listener: (message: ChatMessageType) => void) => void;
  offNewMessage: (listener: (message: ChatMessageType) => void) => void;
  potentialChatParticipants: ChatActorType[];
  refetchChatTimeline: () => void;
  socketIsConnected: boolean;
  sendMessage: ({ chatId, message }: { chatId: string; message: string }) => void;
  updateChatWithLatestMessage: ({
    chatId,
    message,
  }: {
    chatId: string;
    message: LatestChatMessageType;
  }) => void;
  viewerActor: ViewerActorType;
};

const ChatsContext = createContext<ChatsContextProps | null>(null);

export function useChatsContext() {
  const context = useContext(ChatsContext);
  if (!context) {
    throw new Error('useChatsContext must be used within a ChatsContext.Provider');
  }
  return context;
}

export default ChatsContext;
